/**
 * Pricing maths helpers. Pure functions only — no config, no I/O.
 */

/** Round to 2 decimals. The base rounding for decimal currencies. */
export function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Money rounding. Every WeKIT payment is a whole number in its own currency —
 * whole rupees, whole dollars, whole pounds. Nobody should ever see paise or
 * cents on a WeKIT invoice, so a payable amount rounds to the nearest unit
 * regardless of currency. `currency` is kept in the signature because callers
 * pass it and future zero/three-decimal currencies may need it.
 */
export function roundMoney(n: number, _currency?: string): number {
  return Math.round(Number(n) || 0);
}


export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number(n) || 0));
}


/**
 * Money off a base for one rule. Discounts ALWAYS apply to the list price,
 * never to an already-discounted number, so two rules can never compound.
 */
export function discountAmount(
  type: string,
  value: number,
  listPrice: number,
): number {
  const t = String(type || "").toLowerCase();
  if (t === "full") return round2(listPrice);
  if (t === "percent") return round2((listPrice * clamp(value, 0, 100)) / 100);
  if (t === "flat") return clamp(round2(value), 0, round2(listPrice));
  return 0;
}

/** Tax on an amount, for regions where the list price EXCLUDES tax. */
export function taxAmount(
  amount: number,
  ratePercent: number,
  inclusive: boolean,
): number {
  if (inclusive) return 0;
  return round2((round2(amount) * clamp(ratePercent, 0, 100)) / 100);
}

/** Smallest gateway unit — paise for INR, cents for USD. */
export function toMinorUnits(amount: number): number {
  return Math.round(round2(amount) * 100);
}

/** True when `now` falls inside an optional ISO window (both ends inclusive). */
export function withinWindow(
  now: Date,
  validFrom?: string | null,
  validUntil?: string | null,
): { ok: boolean; reason: "not_yet_valid" | "expired" | "ok" } {
  if (validFrom) {
    const from = new Date(validFrom);
    if (!Number.isNaN(from.getTime()) && from.getTime() > now.getTime()) {
      return { ok: false, reason: "not_yet_valid" };
    }
  }
  if (validUntil) {
    const until = new Date(validUntil);
    if (!Number.isNaN(until.getTime()) && until.getTime() < now.getTime()) {
      return { ok: false, reason: "expired" };
    }
  }
  return { ok: true, reason: "ok" };
}

export function normaliseCode(raw: unknown): string {
  return String(raw ?? "").trim().toUpperCase();
}

/** 3–32 chars, A–Z 0–9 hyphen underscore. Matches the checkout contract. */
export const CODE_FORMAT = /^[A-Z0-9_-]{3,32}$/;

export function formatMoney(amount: number, currency: string): string {
  const symbols: Record<string, string> = {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
    AED: "AED ",
    AUD: "A$",
    ZAR: "R",
  };
  const locale = currency === "INR" ? "en-IN" : "en-US";
  const value = roundMoney(amount, currency);
  return `${symbols[currency] ?? `${currency} `}${value.toLocaleString(locale, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  })}`;
}

